import React, { useState } from "react";
import {
  Button,
  Flex,
  NumberInput,
  Space,
  Title,
  Tooltip,
} from "@mantine/core";
import { Circle } from "lucide-react";

interface TransformSectorInCircleProps {
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  selectedDots: Record<string, any>;
  setUndoStack: React.Dispatch<React.SetStateAction<any[]>>;
  t: (key: string) => string;
  px?: string;
}

const TransformSectorInCircle: React.FC<TransformSectorInCircleProps> = ({
  data,
  setData,
  selectedDots,
  setUndoStack,
  px,
  t,
}) => {
  const [maxRadius, setMaxRadius] = useState<number>(500);
  const [baseRadius, setBaseRadius] = useState<number>(300);
  const [rowGrowthFactor, setRowGrowthFactor] = useState<number>(30);
  const [colSpacingFactor, setColSpacingFactor] = useState<number>(0); // New spacing factor

  const transformSectorInCircle = () => {
    setUndoStack((prevStack) => [...prevStack, data]);

    const newData = JSON.parse(JSON.stringify(data));

    const keys = selectedDots;
    const rowsSet = new Set();
    const colsSet = new Set();
    const nameParts = new Set();

    keys.forEach((key: string) => {
      const parts = key.split(":");
      nameParts.add(parts[0]);
      rowsSet.add(parts[1]);
      colsSet.add(parts[2]);
    });

    const rows = Array.from(rowsSet);
    const cols = Array.from(colsSet);
    const namePart = Array.from(nameParts)[0];

    const centerX = 500;
    const centerY = 350;

    const totalCols = cols.length;
    const adjustedCols = totalCols + colSpacingFactor; // Apply spacing factor

    const angleStep = adjustedCols > 1 ? (2 * Math.PI) / adjustedCols : Math.PI;

    rows.forEach((row, rowIdx) => {
      const adjustedRadius = Math.min(
        baseRadius + rowIdx * rowGrowthFactor,
        maxRadius
      );

      cols.forEach((col, colIdx) => {
        const angle = angleStep * (colIdx + colSpacingFactor / 2); // Adjust position by spacing
        const xOffset = centerX + adjustedRadius * Math.cos(angle);
        const yOffset = centerY + adjustedRadius * Math.sin(angle);

        const key = `${namePart}:${row}:${col}`;
        const rect = newData.data[key]?.rect;

        if (rect) {
          rect.x = xOffset.toString();
          rect.y = yOffset.toString();
        }
      });
    });

    setData(newData);
  };

  return (
    <>
      <Title px={px} order={6}>
        {t("scheme.redactor.panelOptions.setSector.sectorInCircle.title")}
      </Title>
      <Space h="xs" />
      <Flex px={px}>
        <Tooltip
          label={t(
            "scheme.redactor.panelOptions.setSector.sectorInCircle.maxRadius"
          )}
        >
          <NumberInput
            size="sm"
            hideControls
            radius={0}
            value={maxRadius}
            min={1}
            onChange={(value) => setMaxRadius(Number(value))}
          />
        </Tooltip>

        <Tooltip
          label={t(
            "scheme.redactor.panelOptions.setSector.sectorInCircle.baseRadius"
          )}
        >
          <NumberInput
            size="sm"
            hideControls
            radius={0}
            value={baseRadius}
            min={1}
            onChange={(value) => setBaseRadius(Number(value))}
          />
        </Tooltip>

        <Tooltip
          label={t(
            "scheme.redactor.panelOptions.setSector.sectorInCircle.rowGrowthFactor"
          )}
        >
          <NumberInput
            size="sm"
            hideControls
            radius={0}
            value={rowGrowthFactor}
            min={1}
            onChange={(value) => setRowGrowthFactor(Number(value))}
          />
        </Tooltip>

        <Tooltip
          label={t(
            "scheme.redactor.panelOptions.setSector.sectorInCircle.colSpacingFactor"
          )}
        >
          <NumberInput
            size="sm"
            hideControls
            radius={0}
            value={colSpacingFactor}
            min={0}
            onChange={(value) => setColSpacingFactor(Number(value))}
          />
        </Tooltip>

        <Button
          size="sm"
          onClick={transformSectorInCircle}
          variant="light"
          color="lime"
          radius={0}
        >
          <Circle size={22} />
        </Button>
      </Flex>
    </>
  );
};

export default React.memo(TransformSectorInCircle);
